/**
 * Program Selection Hook
 *
 * Fetches existing program selection observation and provides
 * submit/validation handlers for the program selection flow.
 * @agentic connections
 */

import { useState, useEffect, useCallback } from "react";
import { useMedplum } from "@medplum/react";
import type {
  CarePlan,
  Observation,
  Patient,
  PractitionerRole,
  ServiceRequest,
} from "@medplum/fhirtypes";
import {
  SERVICE_TYPE_OPTIONS,
  type OperationalProgramCode,
} from "@benchmark/core/constants";
import { getProgramSelectionObservation } from "@benchmark/core/services";
import { submitProgramSelection } from "@benchmark/core/actions";
import { validateIntakeCompletionGate } from "@benchmark/core/validators";
import { createOperationalCarePlan } from "@benchmark/core/workflows";
import { mapServiceNeedsToServiceRequestInputs } from "@benchmark/core/adapters";
import type { ProgramSelectionFormData } from "@benchmark/core/types";
import type { IntakeGateValidationResult } from "@benchmark/core/validators";

/**
 * Result type for the useProgramSelection hook.
 * @agentic connections
 */
export type UseProgramSelectionResult = {
  existingObservation: Observation | undefined;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  gateValidation: IntakeGateValidationResult | null;
  submit: (
    formData: ProgramSelectionFormData,
    patient: Patient,
    practitionerRole: PractitionerRole,
    intakeCarePlan: CarePlan,
    firstActivityOwnerOverride?: { ref: string; display: string },
  ) => Promise<{
    observation?: Observation;
    operationalCarePlan?: CarePlan;
    /** Sibling Triage plan when CoCM-with-referrals creates two plans (D4). */
    siblingTriageCarePlan?: CarePlan;
    createdServiceRequests?: ServiceRequest[];
  }>;
  validate: () => Promise<IntakeGateValidationResult>;
  refetch: () => Promise<void>;
};

/**
 * Hook for managing program selection state and operations.
 * @agentic connections
 */
export function useProgramSelection(
  patientId: string | undefined,
  intakeCarePlanId: string | undefined,
): UseProgramSelectionResult {
  const medplum = useMedplum();
  const [existingObservation, setExistingObservation] = useState<
    Observation | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gateValidation, setGateValidation] =
    useState<IntakeGateValidationResult | null>(null);

  const fetchObservation = useCallback(async () => {
    if (!patientId || !intakeCarePlanId) return;
    setLoading(true);
    setError(null);
    try {
      const obs = await getProgramSelectionObservation(
        medplum,
        patientId,
        intakeCarePlanId,
      );
      setExistingObservation(obs);
      const validation = validateIntakeCompletionGate(obs);
      setGateValidation(validation);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load program selection",
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, patientId, intakeCarePlanId]);

  useEffect(() => {
    queueMicrotask(() => {
      void fetchObservation();
    });
  }, [fetchObservation]);

  const validate =
    useCallback(async (): Promise<IntakeGateValidationResult> => {
      if (!patientId || !intakeCarePlanId) {
        return { isValid: false, errors: ["Missing patient or care plan ID"] };
      }
      const obs = await getProgramSelectionObservation(
        medplum,
        patientId,
        intakeCarePlanId,
      );
      const result = validateIntakeCompletionGate(obs);
      setGateValidation(result);
      return result;
    }, [medplum, patientId, intakeCarePlanId]);

  const submit = useCallback(
    async (
      formData: ProgramSelectionFormData,
      patient: Patient,
      practitionerRole: PractitionerRole,
      intakeCarePlan: CarePlan,
      firstActivityOwnerOverride?: { ref: string; display: string },
    ) => {
      if (!formData.selectedProgram) {
        throw new Error("No program selected");
      }

      setSubmitting(true);
      setError(null);
      try {
        const needCodes =
          formData.serviceNeeds?.length > 0
            ? formData.serviceNeeds.map((n) => n.serviceType)
            : formData.selectedNeeds;

        const reasonText = formData.reasonText?.trim();
        const shouldPersistObservation = Boolean(reasonText);
        let observation: Observation | undefined;
        if (shouldPersistObservation) {
          const result = await submitProgramSelection(medplum, {
            patient,
            practitionerRole,
            intakeCarePlan,
            selectedProgram: formData.selectedProgram,
            reasonCodes: formData.reasonCodes,
            reasonText,
            selectedNeeds: needCodes.length > 0 ? needCodes : undefined,
          });
          observation = result.observation;
        }

        setExistingObservation(observation);

        const serviceRequestDetails =
          formData.serviceNeeds?.length > 0
            ? mapServiceNeedsToServiceRequestInputs(formData.serviceNeeds)
            : needCodes.length > 0
              ? needCodes.map((code) => {
                  const opt = SERVICE_TYPE_OPTIONS.find(
                    (o) => o.value === code,
                  );
                  return { code, display: opt?.label || code };
                })
              : undefined;

        if (
          formData.selectedProgram === "cocm" &&
          formData.includeReferrals &&
          !serviceRequestDetails?.length
        ) {
          throw new Error(
            "CoCM enrollment with referrals requires at least one service need. " +
              "Triage activities cannot be created without service request details.",
          );
        }

        const {
          operationalCarePlan,
          siblingTriageCarePlan,
          createdServiceRequests,
        } = await createOperationalCarePlan(medplum, {
          patient,
          practitionerRole,
          selectedProgram: formData.selectedProgram as OperationalProgramCode,
          serviceRequestDetails,
          programSelectionObservation: observation,
          requiresApproval: formData.requiresApproval,
          firstActivityOwnerOverride,
        });

        const validation = observation
          ? validateIntakeCompletionGate(observation)
          : { isValid: true, errors: [] };
        setGateValidation(validation);

        return {
          observation,
          operationalCarePlan,
          siblingTriageCarePlan,
          createdServiceRequests,
        };
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : "Failed to submit program selection";
        setError(msg);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [medplum],
  );

  return {
    existingObservation,
    loading,
    submitting,
    error,
    gateValidation,
    submit,
    validate,
    refetch: fetchObservation,
  };
}
